import { Fragment, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { Route, Routes, useLocation } from 'react-router';

import {
  ContractIcon,
  ManageNoticeIcon,
  MemberIcon,
  MoneyIcon,
  NoticeIcon,
  ProductIcon,
  ProductManageIcon,
  RateIcon,
} from '@/assets/svg';
import { useMyPermissions } from '@/hooks/queries/universal';
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout';
import GuestLayout from '@/layouts/GuestLayout';
import AdjustmentIndex from '@/pages/AdjustmentIndex';
import ContractIndex from '@/pages/ContractIndex';
import EditUser from '@/pages/EditUser';
import Login from '@/pages/Login';
import Logout from '@/pages/Logout';
import NotFound from '@/pages/NotFound';
import PolicyTemplate from '@/pages/PolicyTemplate';
import NoticeManageForm from '@/pages/admin/notice/NoticeForm';
import NoticeManageList from '@/pages/admin/notice/NoticeList';
import MarketingProductManageIndex from '@/pages/admin/product';
import MarketingProductManageForm from '@/pages/admin/product/ProductForm';
import MarketingProductManageList from '@/pages/admin/product/ProductList';
import MarketingProductManageOrderDetail from '@/pages/admin/product/ProductOrderDetail';
import MarketingProductManageOrderList from '@/pages/admin/product/ProductOrderList';
import UserManageDetail from '@/pages/admin/user/UserDetail';
import UserManageList from '@/pages/admin/user/UserList';
import PartnerNoticeDetail from '@/pages/notice/NoticeDetail';
import PartnerNoticeList from '@/pages/notice/NoticeList';
import MarketingProductIndex from '@/pages/product';
import MarketingProductDetail from '@/pages/product/ProductDetail';
import MarketingProductList from '@/pages/product/ProductList';
import MarketingProductOrder from '@/pages/product/ProductOrder';
import MarketingProductOrderHistory from '@/pages/product/ProductOrderHistory';
import MarketingProductOrderHistoryDetail from '@/pages/product/ProductOrderHistoryDetail';
import MarketingProductSuccess from '@/pages/product/ProductSuccess';
import ShareIndex from '@/pages/share';
import ShareDaily from '@/pages/share/ShareDaily';
import ShareDailyOperation from '@/pages/share/ShareDailyOperation';
import ShareUnit from '@/pages/share/ShareUnit';
import ShareUnitComposition from '@/pages/share/ShareUnitComposition';
import type {
  ChildRoute,
  ClientRouteKey,
  RouteKey,
  RouteMap,
  RouteObject,
} from '@/types/app';

import PolicyLayout from './layouts/PolicyLayout';

// 페이지의 path or url
export const PATH = {
  LOGIN: '/login',
  LOGOUT: '/logout',
  EDIT_USER: '/edit-user',
  POLICY: {
    INDEX: '/policy',
    PRIVATE: '/policy/private',
  },
  NOTICE: {
    INDEX: '/notice',
    DETAIL: '/notice/:id',
  },
  CURRENT_CONTRACT: {
    INDEX: '/contract',
  },
  BRANCH_SHARE: {
    INDEX: '/share',
    DAILY: '/share/daily',
    DAILY_OPERATION: '/share/dailyOperation',
    UNIT: '/share/unit',
    UNIT_COMPOSITION: '/share/unitComposition',
  },
  ADJUSTMENT: {
    INDEX: '/adjustment',
  },
  MARKETING_PRODUCT: {
    INDEX: '/product',
    DETAIL: '/product/:id',
    ORDER: '/product/order',
    SUCCESS: '/product/order/success',
    ORDER_HISTORY: '/product/orderHistory',
    ORDER_HISTORY_DETAIL: '/product/orderHistory/:id',
  },
  NOTICE_MANAGE: {
    INDEX: '/admin/notice',
    REGISTER: '/admin/notice/register',
    DETAIL: '/admin/notice/:id',
  },
  MARKETING_PRODUCT_MANAGE: {
    INDEX: '/admin/product',
    REGISTER: '/admin/product/register',
    DETAIL: '/admin/product/:id',
    ORDER: '/admin/product/order',
    ORDER_DETAIL: '/admin/product/order/:id',
  },
  USER_MANAGE: {
    INDEX: '/admin/user',
    DETAIL: '/admin/user/:id',
  },
} as const;

// 메뉴별 부모 카테고리, path, icon, page component 지정
// children: 같은 키를 사용하면서 타이틀이 동일할 때
// sibling: 같은 키를 사용하지만 타이틀이 다를 때
export const ROUTES: RouteMap = {
  LOGIN: {
    title: '로그인',
    path: PATH.LOGIN,
    element: <Login />,
  },
  LOGOUT: {
    title: '로그아웃',
    path: PATH.LOGOUT,
    element: <Logout />,
  },
  EDIT_USER: {
    title: '정보 수정하기',
    path: PATH.EDIT_USER,
    element: <EditUser />,
  },
  POLICY: {
    title: '이용 약관',
    path: PATH.POLICY.INDEX,
    element: <PolicyTemplate routeKey="POLICY" />,
  },
  POLICY_PRIVATE: {
    title: '개인정보 처리방침',
    path: PATH.POLICY.PRIVATE,
    element: <PolicyTemplate routeKey="POLICY_PRIVATE" />,
  },
  NOTICE: {
    title: '공지사항',
    menuGroupKey: 'PARTNER',
    path: PATH.NOTICE.INDEX,
    icon: <NoticeIcon />,
    element: <PartnerNoticeList />,
    sibling: [
      {
        title: '공지사항 상세',
        path: PATH.NOTICE.DETAIL,
        element: <PartnerNoticeDetail />,
      },
    ],
  },
  CURRENT_CONTRACT: {
    title: '판매 현황',
    menuGroupKey: 'PARTNER',
    path: PATH.CURRENT_CONTRACT.INDEX,
    icon: <ContractIcon />,
    element: <ContractIndex />,
    useBranchSelector: true,
  },
  BRANCH_SHARE: {
    title: '지점 점유율',
    menuGroupKey: 'PARTNER',
    path: PATH.BRANCH_SHARE.INDEX,
    icon: <RateIcon />,
    element: <ShareIndex />,
    useBranchSelector: true,
    children: [
      { path: PATH.BRANCH_SHARE.DAILY, element: <ShareDaily /> },
      {
        path: PATH.BRANCH_SHARE.DAILY_OPERATION,
        element: <ShareDailyOperation />,
      },
      { path: PATH.BRANCH_SHARE.UNIT, element: <ShareUnit /> },
      {
        path: PATH.BRANCH_SHARE.UNIT_COMPOSITION,
        element: <ShareUnitComposition />,
      },
    ],
  },
  ADJUSTMENT: {
    title: '정산 내역',
    menuGroupKey: 'PARTNER',
    path: PATH.ADJUSTMENT.INDEX,
    icon: <MoneyIcon />,
    element: <AdjustmentIndex />,
  },
  MARKETING_PRODUCT: {
    title: '마케팅 상품',
    menuGroupKey: 'PARTNER',
    path: PATH.MARKETING_PRODUCT.INDEX,
    icon: <ProductIcon />,
    element: <MarketingProductIndex />,
    children: [
      {
        path: PATH.MARKETING_PRODUCT.INDEX,
        element: <MarketingProductList />,
      },
      {
        path: PATH.MARKETING_PRODUCT.ORDER_HISTORY,
        element: <MarketingProductOrderHistory />,
      },
    ],
    sibling: [
      {
        title: '상품 상세보기',
        path: PATH.MARKETING_PRODUCT.DETAIL,
        element: <MarketingProductDetail />,
      },
      {
        title: '상품 신청하기',
        path: PATH.MARKETING_PRODUCT.ORDER,
        element: <MarketingProductOrder />,
      },
      {
        title: '신청이 완료되었습니다',
        path: PATH.MARKETING_PRODUCT.SUCCESS,
        element: <MarketingProductSuccess />,
      },
      {
        title: '신청내역 상세',
        path: PATH.MARKETING_PRODUCT.ORDER_HISTORY_DETAIL,
        element: <MarketingProductOrderHistoryDetail />,
      },
    ],
  },
  NOTICE_MANAGE: {
    title: '공지사항 관리',
    menuGroupKey: 'ADMIN',
    path: PATH.NOTICE_MANAGE.INDEX,
    icon: <ManageNoticeIcon />,
    element: <NoticeManageList />,
    sibling: [
      {
        title: '공지사항 작성',
        path: PATH.NOTICE_MANAGE.REGISTER,
        element: <NoticeManageForm />,
      },
      {
        title: '공지사항 수정',
        path: PATH.NOTICE_MANAGE.DETAIL,
        element: <NoticeManageForm />,
      },
    ],
  },
  MARKETING_PRODUCT_MANAGE: {
    title: '마케팅 상품 관리',
    menuGroupKey: 'ADMIN',
    path: PATH.MARKETING_PRODUCT_MANAGE.INDEX,
    icon: <ProductManageIcon />,
    element: <MarketingProductManageIndex />,
    children: [
      {
        path: PATH.MARKETING_PRODUCT_MANAGE.INDEX,
        element: <MarketingProductManageList />,
      },
      {
        path: PATH.MARKETING_PRODUCT_MANAGE.ORDER,
        element: <MarketingProductManageOrderList />,
      },
    ],
    sibling: [
      {
        title: '마케팅 상품 등록',
        path: PATH.MARKETING_PRODUCT_MANAGE.REGISTER,
        element: <MarketingProductManageForm />,
      },
      {
        title: '마케팅 상품 상세',
        path: PATH.MARKETING_PRODUCT_MANAGE.DETAIL,
        element: <MarketingProductManageForm />,
      },
      {
        title: '마케팅 상품 신청 내역',
        path: PATH.MARKETING_PRODUCT_MANAGE.ORDER_DETAIL,
        element: <MarketingProductManageOrderDetail />,
      },
    ],
  },
  USER_MANAGE: {
    title: '유저 관리',
    menuGroupKey: 'ADMIN',
    path: PATH.USER_MANAGE.INDEX,
    icon: <MemberIcon />,
    element: <UserManageList />,
    sibling: [
      {
        title: '유저 상세',
        path: PATH.USER_MANAGE.DETAIL,
        element: <UserManageDetail />,
      },
    ],
  },
} as const;

const guestRouteKeys: string[] = ['LOGIN'] as RouteKey[]; // GuestLayout을 사용할 키 목록
const policyRouteKeys: string[] = ['POLICY', 'POLICY_PRIVATE'] as RouteKey[];
const clientRouteKeys: string[] = [
  'LOGIN',
  'LOGOUT',
  'EDIT_USER',
  ...policyRouteKeys,
] as ClientRouteKey[]; // 권한 체크를 하지 않을 라우트 키 목록

export default () => {
  const { pathname } = useLocation();
  const { availableMenuKeys } = useMyPermissions();

  const renderChildren = (children?: ChildRoute[]) => {
    if (!children) return null;

    return children.map((child) => (
      <Route key={child.path} path={child.path} element={child.element}>
        {renderChildren(child.children)}
      </Route>
    ));
  };

  const renderRoute = (key: string, route: RouteObject) => {
    const isGuest = guestRouteKeys.some(
      (guestRouteKey) => guestRouteKey === key
    );
    const isPolicy = policyRouteKeys.some(
      (policyRouteKey) => policyRouteKey === key
    );

    return (
      <Route
        key={route.path}
        path={route.path}
        element={
          isGuest ? (
            <GuestLayout>{route.element}</GuestLayout>
          ) : isPolicy ? (
            <PolicyLayout>{route.element}</PolicyLayout>
          ) : (
            <AuthenticatedLayout
              title={route.title}
              useBranchSelector={route.useBranchSelector}
            >
              {route.element}
            </AuthenticatedLayout>
          )
        }
        index={false}
      >
        {renderChildren(route.children)}
      </Route>
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (
      process.env.NODE_ENV === 'production' &&
      process.env.REACT_APP_GTAG_ID
    ) {
      ReactGA.initialize(process.env.REACT_APP_GTAG_ID);
    }
  }, []);

  return (
    <Routes>
      {Object.entries(ROUTES)
        .filter(
          ([key]) =>
            // 권한체크를 하지 않는 화면이거나 권한이 있는 메뉴만 필터링
            clientRouteKeys.some((clientRouteKey) => clientRouteKey === key) ||
            availableMenuKeys.some(
              (availableMenuKey) => availableMenuKey === key
            )
        )
        .map(([key, route]) => {
          return (
            <Fragment key={key}>
              {renderRoute(key, route)}

              {route.sibling?.map((siblingRoute) => {
                return renderRoute(key, siblingRoute);
              })}
            </Fragment>
          );
        })}

      <Route path="/*" element={<NotFound />} />
    </Routes>
  );
};
